import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';
import { 
  UsersIcon, 
  CreditCardIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowLeftIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

interface AdminStats {
  total_users: number;
  active_subscribers: number;
  total_attempts: number;
  recent_registrations: number;
  admin_count: number;
}

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAdminStats();
  }, []);

  const loadAdminStats = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getAdminStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if not admin
  if (user && !user.is_admin) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have admin privileges to access this page.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-4 py-2 bg-mango-600 text-white rounded-lg hover:bg-mango-500 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-mango-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading admin data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Error Loading Admin Data
            </h2>
            <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
            <button
              onClick={loadAdminStats}
              className="px-4 py-2 bg-mango-600 text-white rounded-lg hover:bg-mango-500"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-mango-600 dark:hover:text-mango-400 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
                Back to Dashboard
              </Link>
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
              <div className="flex items-center">
                <div className="h-10 w-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9H21ZM15 10H21V12H15V10ZM5 7L3 7V9H9V7.5L5 7ZM3 10H9V12H3V10ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"/>
                  </svg>
                </div>
                <div className="ml-3">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Admin Panel
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Platform Management
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                ADMIN
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <UsersIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.total_users || 0}
                </p>
                <p className="text-gray-600 dark:text-gray-400">Total Users</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CreditCardIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.active_subscribers || 0}
                </p>
                <p className="text-gray-600 dark:text-gray-400">Subscribers</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <ChartBarIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.total_attempts || 0}
                </p>
                <p className="text-gray-600 dark:text-gray-400">Total Attempts</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <CalendarIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.recent_registrations || 0}
                </p>
                <p className="text-gray-600 dark:text-gray-400">New Users (7d)</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9H21ZM15 10H21V12H15V10Z"/>
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.admin_count || 0}
                </p>
                <p className="text-gray-600 dark:text-gray-400">Admin Users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Management Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">User Management</h2>
            </div>
            <div className="p-6 space-y-4">
              <Link 
                to="/admin/users"
                className="block w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-mango-500 hover:bg-mango-50 dark:hover:bg-mango-900/20 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UsersIcon className="h-6 w-6 text-gray-400 mr-3" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Manage Users</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        View, edit, and manage user accounts and privileges
                      </p>
                    </div>
                  </div>
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">System Analytics</h2>
            </div>
            <div className="p-6 space-y-4">
              <Link
                to="/admin/server-health"
                className="block w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-mango-500 hover:bg-mango-50 dark:hover:bg-mango-900/20 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ServerIcon className="h-6 w-6 text-gray-400 mr-3" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Server Health</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Check backend, database, Redis, locks, memory, and disk
                      </p>
                    </div>
                  </div>
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>

              <button 
                className="block w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-mango-500 hover:bg-mango-50 dark:hover:bg-mango-900/20 transition-colors text-left opacity-50 cursor-not-allowed"
                disabled
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-6 w-6 text-gray-400 mr-3" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Platform Analytics</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Detailed analytics and reporting (Coming Soon)
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;